// ADV-1319 / ADR-0055: attach the authenticated user + their tenant context
// (account, business, roles) to every Dash0 RUM signal. Without this, Dash0
// sessions/logs/spans carry no "who / which account / which business" context,
// so per-user and per-tenant triage is impossible.
//
// Split out from `observability.ts` so it can be wired to the auth lifecycle
// (call on login, clear on logout) independently of the one-time SDK `init()`.
//
// PII posture (mirrors the `urlAttributeScrubber` in observability.ts): we send
// the opaque user UUID + display name + roles, NOT the raw email. The account
// and business are sent as the `adva.*` house-namespace tenant attributes the
// backend spans already use (ADR-0054), so "everything for account X / business
// Y" queries span the browser and backend surfaces identically.
import {
  addSignalAttribute,
  identify,
  removeSignalAttribute,
  terminateSession,
} from "@dash0/sdk-web";

/** The RUM-relevant slice of the authenticated user + tenant context. */
export interface RumUserContext {
  /** Opaque user UUID — `user.id` (not the email; non-PII). */
  userId: string;
  /** Display name — `user.full_name`. */
  displayName?: string;
  /** Account UUID the user is operating in — `adva.account_id` (immutable). */
  accountId?: string;
  /** Human-readable account name — `adva.account_name`. */
  accountName?: string;
  /** Account slug — `adva.account_slug`. */
  accountSlug?: string;
  /** Active business UUID — `adva.business_id` (immutable). */
  businessId?: string;
  /** Human-readable business name — `adva.business_name`. */
  businessName?: string;
  /** Business slug — `adva.business_slug`. */
  businessSlug?: string;
  /** Unified system role (e.g. `admin`, `user`) — `adva.system_role` + `user.roles`. */
  systemRole?: string;
  /** End-user role within a business (e.g. `manager`) — `adva.end_user_role`. */
  endUserRole?: string | null;
  /** Account-scoped role claim — `adva.account_role`. */
  accountRole?: string;
  /** Business-scoped role claim — `adva.business_role`. */
  businessRole?: string;
}

/** Tenant/role signal attributes set per-session; removed on logout. */
const TENANT_SIGNAL_ATTRS = [
  "adva.account_id",
  "adva.account_name",
  "adva.account_slug",
  "adva.business_id",
  "adva.business_name",
  "adva.business_slug",
  "adva.account_role",
  "adva.business_role",
  "adva.system_role",
  "adva.end_user_role",
] as const;

function setIfPresent(key: string, value: string | null | undefined): void {
  if (value) addSignalAttribute(key, value);
  else removeSignalAttribute(key);
}

/**
 * Identify the logged-in user + attach their account/business/role context to
 * every subsequent RUM signal. Safe to call repeatedly (e.g. on business
 * switch) — each call replaces the prior context. No-ops harmlessly when the
 * Dash0 SDK was never initialised (local dev without the ingest token).
 */
export function setRumUser(user: RumUserContext): void {
  const roles = [user.systemRole, user.endUserRole].filter(
    (r): r is string => !!r,
  );
  // user.id / user.full_name / user.roles — NO email (PII).
  identify(user.userId, {
    ...(user.displayName ? { fullName: user.displayName } : {}),
    ...(roles.length ? { roles } : {}),
  });

  // Tenant context on the adva.* house namespace (ADR-0054) — the high-value
  // "which account / which business" join keys for cross-surface triage.
  // Immutable ids are the precise join keys; names + slugs make the telemetry
  // readable in Dash0/Honeycomb dashboards and alerts.
  setIfPresent("adva.account_id", user.accountId);
  setIfPresent("adva.account_name", user.accountName);
  setIfPresent("adva.account_slug", user.accountSlug);
  setIfPresent("adva.business_id", user.businessId);
  setIfPresent("adva.business_name", user.businessName);
  setIfPresent("adva.business_slug", user.businessSlug);
  setIfPresent("adva.account_role", user.accountRole);
  setIfPresent("adva.business_role", user.businessRole);
  setIfPresent("adva.system_role", user.systemRole);
  setIfPresent("adva.end_user_role", user.endUserRole ?? undefined);
}

/**
 * Clear the user + tenant context on logout: end the RUM session (so the next
 * session starts anonymous and is not attributed to the previous user) and drop
 * all tenant signal attributes.
 */
export function clearRumUser(): void {
  terminateSession();
  for (const attr of TENANT_SIGNAL_ATTRS) removeSignalAttribute(attr);
}
